import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
    AlertController,
    IonBackButton,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonListHeader,
    IonTitle,
    IonToolbar,
    ModalController,
} from '@ionic/angular/standalone';
import { EditCalculatedSignalComponent } from './edit-calculated-signal.component';
import { CalculatedSignalProvider } from '../../providers/calculated-signal-provider';

@Component({
    selector: 'app-tab-calculated-signals',
    host: { class: 'ion-page' },
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonBackButton, IonListHeader, IonItem, IonLabel, IonButton, IonIcon],
    template: `
        <ion-header>
            <ion-toolbar>
                <ion-buttons slot="start">
                    <ion-back-button defaultHref="/tabs"></ion-back-button>
                </ion-buttons>
                <ion-title>Calculated Signals</ion-title>
            </ion-toolbar>
        </ion-header>

        <ion-content>
            <ion-list-header>
                <ion-label>Defined Formulas</ion-label>
                <ion-button (click)="openEditor()">Add Signal</ion-button>
            </ion-list-header>

            @if (signals().length) {
                @for (sig of signals(); track sig.id) {
                    <ion-item>
                        <div class="label-container">
                            <ion-label class="ion-text-wrap">
                                <h2>{{ sig.name }}</h2>
                                <p>{{ sig.formula }}</p>
                            </ion-label>
                            <ion-button (click)="openEditor(sig.id)" fill="clear" class="rename-button">
                                <ion-icon slot="icon-only" name="pencil-outline"></ion-icon>
                            </ion-button>
                            <ion-button (click)="confirmDeleteSignal(sig.id)" fill="clear" class="delete-button">
                                <ion-icon slot="icon-only" src="assets/icons/bin.svg"></ion-icon>
                            </ion-button>
                        </div>
                    </ion-item>
                }
            } @else {
                <ion-item>
                    <ion-label>No Calculated Signals created yet.</ion-label>
                </ion-item>
            }
        </ion-content>
    `,
    styles: `
        ion-list-header {
            font-size: 1rem;
        }

        ion-item {
            --highlight-background: transparent !important;

            div.label-container {
                display: flex;
                flex-direction: row;
                justify-content: flex-start;
                flex-grow: 1;
                align-items: center;
            }

            ion-button.rename-button {
                --padding-end: 8px;
                --padding-start: 12px;
                margin-left: auto;
            }

            ion-button.delete-button {
                --padding-end: 8px;
                --padding-start: 12px;
                margin-left: 0.5rem;
            }
        }
    `,
})
export class TabCalculatedSignalsPageComponent {
    readonly signals;

    private readonly alertCtrlr = inject(AlertController);
    private readonly modalCtrl = inject(ModalController);
    private readonly calculatedSignalProvider = inject(CalculatedSignalProvider);

    constructor() {
        this.signals = this.calculatedSignalProvider.signals;
    }

    async openEditor(id?: string) {
        const modal = await this.modalCtrl.create({
            component: EditCalculatedSignalComponent,
            componentProps: { editId: id },
            backdropDismiss: false,
        });

        await modal.present();
    }

    async confirmDeleteSignal(id: string) {
        const signal = this.signals().find((s) => s.id === id);
        if (!signal) return;

        const alert = await this.alertCtrlr.create({
            header: 'Delete Calculated Signal',
            message: `Are you sure you want to delete the formula "${signal.name}"?`,
            buttons: [
                {
                    text: 'Cancel',
                    role: 'cancel',
                },
                {
                    text: 'Delete',
                    role: 'destructive',
                    handler: () => {
                        this.calculatedSignalProvider.removeSignal(id);
                    },
                },
            ],
        });
        await alert.present();
    }
}
